import { fetcherConfig } from '../../lib/api-fetcher';
import { useState, useRef } from 'react';
import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router';
import { usePageTitle } from '../../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Mail, Lock, Eye, EyeOff, KeyRound } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { FormField, Input } from '../../components/ui/FormField';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Stack } from '../../components/layout/Stack';
import { createAuthResource, type Session, type MfaChallengeResponse } from '@repo/api-client';
import { getSession, setSession } from '../../lib/session';
import { sessionQueryKey } from '../../lib/query-client';
import { useToast } from '../../components/ui/Toast';

const authResource = createAuthResource(fetcherConfig);

const loginFormConfig = defineFormPage({
  resource: 'auth-login',
  defaultValues: { email: '', password: '' },
  schema: z.object({
    email: z.string().email('Enter a valid email'),
    password: z.string().min(1, 'Password is required'),
  }),
});

export const Route = createFileRoute('/_public/login')({
  beforeLoad: () => {
    const session = getSession();
    if (session) {
      if (session.requiresPasswordReset) {
        throw redirect({ to: '/force-password-change' });
      }
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

function triggerShake() {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
    return;
  }
  setTimeout(() => {
    const card = document.getElementById('auth-card');
    if (card) {
      card.classList.remove('kraphene-wobble');
      void card.offsetWidth;
      card.classList.add('kraphene-wobble');
    }
  }, 50);
}

function LoginPage() {
  usePageTitle('Login');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [isPendingLogin, setIsPendingLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeResponse | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const requestSeqRef = useRef(0);

  const { form, isSubmitting: isFormSubmitting, fieldError: fe } = useFormPage(
    loginFormConfig,
    { mutateAsync: async (v) => v },
  );

  const isSubmitting = isFormSubmitting || isPendingLogin;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPendingLogin) return;

    form.setErrorMap({ onSubmit: undefined });
    setSubmitError(undefined);
    await form.handleSubmit();
    if (!form.state.canSubmit) {
      triggerShake();
      return;
    }

    setIsPendingLogin(true);
    const currentSeq = ++requestSeqRef.current;

    try {
      const res = await authResource.login({
        email: form.getFieldValue('email'),
        password: form.getFieldValue('password'),
      });

      if (currentSeq !== requestSeqRef.current) return;

      if ('mfaRequired' in res && res.mfaRequired) {
        setMfaChallenge(res);
        toast({
          type: 'info',
          title: 'Two-factor Authentication',
          description: 'Please enter the 6-digit code from your authenticator app',
        });
        return;
      }

      const session = res as Session;
      setSession(session);
      qc.setQueryData(sessionQueryKey, session);
      toast({
        type: 'success',
        title: 'Success',
        description: 'Logged in successfully',
      });
      if (session.requiresPasswordReset) {
        void navigate({ to: '/force-password-change' });
      } else {
        void navigate({ to: '/' });
      }
    } catch (err: unknown) {
      if (currentSeq !== requestSeqRef.current) return;

      console.error('Login failed:', err);
      let msg = 'Login failed';
      if (err instanceof Error) {
        msg = err.message;
      } else if (typeof err === 'object' && err !== null) {
        if ('message' in err && typeof err.message === 'string') {
          msg = err.message;
        } else if ('error' in err && typeof err.error === 'string') {
          msg = err.error;
        }
      }
      setSubmitError(msg);
      toast({
        type: 'error',
        title: 'Login failed',
        description: msg,
      });
      triggerShake();
    } finally {
      if (currentSeq === requestSeqRef.current) {
        setIsPendingLogin(false);
      }
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPendingLogin || !mfaChallenge?.mfaToken) return;

    if (!totpCode || totpCode.trim().length !== 6) {
      setSubmitError('Please enter a valid 6-digit code');
      triggerShake();
      return;
    }

    setIsPendingLogin(true);
    setSubmitError(undefined);

    try {
      const session = await authResource.exchangeMfa({
        mfaToken: mfaChallenge.mfaToken,
        totpCode: totpCode.trim(),
      });

      setSession(session);
      qc.setQueryData(sessionQueryKey, session);
      toast({
        type: 'success',
        title: 'Success',
        description: 'Logged in successfully',
      });
      if (session.requiresPasswordReset) {
        void navigate({ to: '/force-password-change' });
      } else {
        void navigate({ to: '/' });
      }
    } catch (err: unknown) {
      console.error('MFA verification failed:', err);
      let msg = 'Invalid verification code';
      if (err instanceof Error) {
        msg = err.message;
      } else if (typeof err === 'object' && err !== null) {
        if ('message' in err && typeof err.message === 'string') {
          msg = err.message;
        } else if ('error' in err && typeof err.error === 'string') {
          msg = err.error;
        }
      }
      setSubmitError(msg);
      toast({
        type: 'error',
        title: 'Verification failed',
        description: msg,
      });
      triggerShake();
    } finally {
      setIsPendingLogin(false);
    }
  };

  if (mfaChallenge) {
    return (
      <AuthLayout title="Two-factor authentication">
        <form onSubmit={handleMfaSubmit}>
          <Stack gap="4">
            <p className="text-sm text-text-muted text-center">
              Enter the 6-digit verification code from your authenticator app for{' '}
              <span className="font-semibold text-text-primary">{mfaChallenge.email}</span>.
            </p>

            <FormField label="Verification code" required htmlFor="totpCode">
              <Input
                id="totpCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                disabled={isSubmitting}
                placeholder="123456"
                startIcon={<KeyRound className="w-5 h-5" />}
              />
            </FormField>

            {submitError && (
              <p className="text-xs text-danger-text text-center" role="alert">
                {submitError}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={isSubmitting}
              className="w-full"
            >
              Verify & sign in
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setMfaChallenge(null);
                setTotpCode('');
                setSubmitError(undefined);
              }}
              disabled={isSubmitting}
              className="w-full text-text-muted hover:text-text-primary"
            >
              Back to login
            </Button>
          </Stack>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Welcome back">
      <form onSubmit={handleSubmit}>
        <Stack gap="4">
          <form.Field
            name="email"
            validators={{
              onChange: ({ value }: { value: any }) => {
                const res = z.string().email('Enter a valid email').safeParse(value);
                return res.success ? undefined : res.error.issues[0].message;
              }
            }}
          >
            {(field: any) => {
              const error = fe(field.state.meta);
              return (
                <FormField label="Email" required error={error} htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={!!error}
                    disabled={isSubmitting}
                    placeholder="you@company.com"
                    startIcon={<Mail className="w-5 h-5" />}
                  />
                </FormField>
              );
            }}
          </form.Field>

          <form.Field
            name="password"
            validators={{
              onChange: ({ value }: { value: any }) => {
                const res = z.string().min(1, 'Password is required').safeParse(value);
                return res.success ? undefined : res.error.issues[0].message;
              }
            }}
          >
            {(field: any) => {
              const error = fe(field.state.meta);
              return (
                <Stack gap="2">
                  <FormField label="Password" required error={error} htmlFor="password">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={!!error}
                      disabled={isSubmitting}
                      placeholder="••••••••"
                      startIcon={<Lock className="w-5 h-5" />}
                      endAdornment={
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword((prev) => !prev)}
                          disabled={isSubmitting}
                          className="h-7 w-7 p-0 text-text-muted hover:text-text-primary rounded focus:ring-1 focus:ring-accent-500 disabled:opacity-50"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          tabIndex={0}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" aria-hidden="true" />
                          ) : (
                            <Eye className="w-4 h-4" aria-hidden="true" />
                          )}
                        </Button>
                      }
                    />
                  </FormField>
                  <div className="flex justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-accent-500 hover:text-accent-600 transition-colors font-medium py-1.5 px-1 inline-flex items-center min-h-[36px] sm:min-h-0"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </Stack>
              );
            }}
          </form.Field>

          {submitError && (
            <p className="text-xs text-danger-text text-center" role="alert">
              {submitError}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={isSubmitting}
            className="w-full"
          >
            Sign in
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
